/**
 * Universal phone-number input used across the platform.
 *
 *   • Country-code dropdown (default = India +91)
 *   • National-number input (digits only)
 *   • Emits the full E.164 string ("+91XXXXXXXXXX") via onChange — that is
 *     also the canonical format saved to the backend.
 *   • Reading an existing record: pass the stored E.164 string as `value` and
 *     the component auto-splits it into dial code + national number.
 *
 * Drop-in replacement for any <input type="tel" /> across the codebase.
 *
 * Props:
 *   value          : current full E.164 string ("" for empty)
 *   onChange       : (e164: string) => void
 *   onChangeNative : optional — receives a synthetic event so existing
 *                    `onChange(e) => set(e.target.value)` handlers keep working
 *   required       : forwarded to the underlying <input>
 *   disabled       : forwarded
 *   placeholder    : forwarded (default "98765 43210")
 *   testid         : prefix for data-testid attrs ("{testid}-dial",
 *                    "{testid}-number")
 *   className      : extra classes for the outer wrapper
 *   nationalMaxLength : optional national-number length cap (default 15)
 *   compact        : true → tighter padding for inline use
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { DIAL_CODES, DEFAULT_DIAL, parsePhone, formatPhone } from "@/lib/dialCodes";

export function PhoneInput({
    value, onChange, onChangeNative,
    required = false, disabled = false,
    placeholder = "98765 43210",
    testid = "phone",
    className = "",
    nationalMaxLength = 15,
    compact = false,
    autoComplete = "tel",
    name = "phone",
}) {
    // Track dial code & national number in local state — kept in sync with
    // `value` (controlled input) and always emit the joined E.164 upstream.
    const parsed = useMemo(() => parsePhone(value), [value]);
    const [dial, setDial] = useState(parsed.dial || DEFAULT_DIAL);
    const [national, setNational] = useState(parsed.national || "");
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ddRef = useRef(null);

    // When the controlled `value` is updated externally (e.g. form reset),
    // sync the local pieces. We skip this when the change came FROM us
    // (joined matches current state) to preserve focus/cursor.
    useEffect(() => {
        const joined = formatPhone(dial, national);
        if (value !== joined) {
            const p = parsePhone(value);
            setDial(p.dial || DEFAULT_DIAL);
            setNational(p.national || "");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // Close dropdown on outside click
    useEffect(() => {
        const onDocClick = (e) => {
            if (ddRef.current && !ddRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    const emit = (nextDial, nextNational) => {
        const joined = formatPhone(nextDial, nextNational);
        onChange?.(joined);
        if (onChangeNative) {
            onChangeNative({ target: { value: joined, name } });
        }
    };

    const handleNational = (e) => {
        // Strip everything except digits for storage normalization, but
        // preserve the user's typing experience with spaces/dashes by
        // letting them enter freely and stripping on emit.
        const raw = e.target.value;
        const digits = raw.replace(/\D/g, "").slice(0, nationalMaxLength);
        setNational(digits);
        emit(dial, digits);
    };

    const selectedCountry = DIAL_CODES.find(c => c.dial === dial) || DIAL_CODES[0];
    const padding = compact ? "py-2 px-2.5" : "py-3 px-4";
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return DIAL_CODES;
        return DIAL_CODES.filter(c =>
            c.name.toLowerCase().includes(q)
            || c.dial.includes(q)
            || c.code.toLowerCase().includes(q),
        );
    }, [search]);

    return (
        <div className={`flex items-stretch ${className}`} ref={ddRef}>
            {/* Country selector */}
            <div className="relative">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setOpen(v => !v)}
                    data-testid={`${testid}-dial`}
                    className={`flex items-center gap-1.5 ${padding} bg-background border border-r-0 border-border hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary outline-none text-sm rounded-l-md text-foreground transition-colors disabled:opacity-60`}
                    aria-haspopup="listbox"
                    aria-expanded={open}>
                    <span className="text-base leading-none">{selectedCountry.flag}</span>
                    <span className="font-mono text-xs">{selectedCountry.dial}</span>
                    <ChevronDown className="w-3 h-3 text-muted-foreground"/>
                </button>
                {open && (
                    <div className="absolute z-50 top-full left-0 mt-1 w-72 bg-card border border-border rounded-lg shadow-lawos-lg max-h-72 overflow-hidden flex flex-col"
                        data-testid={`${testid}-dial-popover`}>
                        <div className="p-2 border-b border-border flex items-center gap-1.5">
                            <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0"/>
                            <input
                                autoFocus
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search country or dial code…"
                                className="bg-transparent outline-none flex-1 text-sm text-foreground placeholder:text-muted-foreground/60"
                                data-testid={`${testid}-dial-search`}/>
                        </div>
                        <div className="overflow-y-auto">
                            {filtered.length === 0 && (
                                <div className="px-3 py-3 text-xs text-muted-foreground">No matches.</div>
                            )}
                            {filtered.map(c => (
                                <button
                                    key={c.code + c.dial}
                                    type="button"
                                    onClick={() => {
                                        setDial(c.dial); setSearch(""); setOpen(false);
                                        emit(c.dial, national);
                                    }}
                                    data-testid={`${testid}-dial-option-${c.code}`}
                                    className={`w-full px-3 py-2 flex items-center gap-2 text-sm text-left hover:bg-muted transition-colors ${c.dial === dial ? "bg-primary/10 text-primary" : "text-foreground"}`}>
                                    <span className="text-base leading-none">{c.flag}</span>
                                    <span className="flex-1 truncate">{c.name}</span>
                                    <span className="font-mono text-xs text-muted-foreground">{c.dial}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {/* National number */}
            <input
                type="tel"
                inputMode="tel"
                autoComplete={autoComplete}
                name={name}
                required={required}
                disabled={disabled}
                value={national}
                onChange={handleNational}
                placeholder={placeholder}
                data-testid={`${testid}-number`}
                className={`flex-1 min-w-0 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none ${padding} text-sm rounded-r-md text-foreground disabled:opacity-60`}
            />
        </div>
    );
}

export default PhoneInput;
